import React, { useMemo } from 'react';
import {forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from 'react';

// add loader for monaco editor to prevent loading from CDN
import { loader } from "@monaco-editor/react";
import * as monaco from "monaco-editor";

import Editor from '@monaco-editor/react';

import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';

// This call forces the monaco editor to load from a local instance and not from the CDN
loader.config({ monaco });

// Now set up worker configuration to fix "unexpected usage" and worker config errors
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker'
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker'
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker'
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker'
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker'
import { currentUser, UserPreference } from 'system/InfegyUser';

export enum QueryErrorStatus {
  LOADING = "LOADING",
  ERROR = "ERROR",
  VERIFIED = "VERIFIED"
}

export interface BooleanQueryEditorProps {
  height: number;
  content: string;
  setContent?: (content: string) => void;

  errorStatus?: QueryErrorStatus;

  language?: string;
  readOnly?: boolean;
  placeholder?: string;

  actions?: React.ReactNode;
}

self.MonacoEnvironment = {
  getWorker(_, label) {
    if (label === 'json') {
      return new jsonWorker()
    }
    if (label === 'css' || label === 'scss' || label === 'less') {
      return new cssWorker()
    }
    if (label === 'html' || label === 'handlebars' || label === 'razor') {
      return new htmlWorker()
    }
    if (label === 'typescript' || label === 'javascript') {
      return new tsWorker()
    }
    return new editorWorker()
  }
}
// end of worker configuration

// Language definition for our Boolean Search Structure
const registerBooleanSearchLanguage = () => {
  monaco.languages.register({ id: 'infegyBoolean' });

  monaco.languages.setLanguageConfiguration('infegyBoolean', {
    brackets: [
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: '“', close: '”' },
      { open: '\'', close: '\'' },
    ],
    surroundingPairs: [
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: '“', close: '”' },
      { open: '\'', close: '\'' },
    ],
  });

  // Define the tokenization rules
  monaco.languages.setMonarchTokensProvider('infegyBoolean', {
    keywords: [
      'AND', 'OR', 'NOT',
    ],

    operators: [],

    tokenizer: {
      root: [
        // Boolean operators (case insensitive)
        [/\b(?:AND|OR|NOT)\b/i, 'keyword.boolean'],

        // Quoted strings
        [/["“”]/, 'string', '@string_double'],
        [/'/, 'string', '@string_single'],

        // Non-terminated strings
        [/"([^"“”\\]|\\.)*$/, 'invalid'],
        [/'([^'\\]|\\.)*$/, 'invalid'],

        // Numbers
        [/\d+/, 'number'],

        // Parentheses
        [/[()]/, '@brackets'],

        // Field names (word followed by colon)
        [/(author|bio|handle):/, 'type.identifier'],

        // handles and hashtags (starting with @ or #)
        [/@\w+/, 'keyword.unique'],
        [/#\w+/, 'keyword.unique'],

        // Proximity operators
        [/\bNEAR\/\d+\b/i, 'keyword.proximity'],
        [/\bONEAR\/\d+\b/i, 'keyword.proximity'],

        // Wildcards
        [/[*?]/, 'keyword.wildcard'],

        // Regular identifiers
        [/[a-zA-Z_]\w*/, 'identifier'],

        // Whitespace
        [/[ \t\r\n]+/, 'white'],

      ],

      string_double: [
        [/[^\\"“”]+/, 'string'],
        [/\\./, 'string.escape.invalid'],
        [/["“”]/, 'string', '@pop']
      ],

      string_single: [
        [/[^\\']+/, 'string'],
        [/\\./, 'string.escape.invalid'],
        [/'/, 'string', '@pop']
      ],
    },
  });


  // Define custom theme for boolean search
  monaco.editor.defineTheme('booleanSearchTheme', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'keyword.boolean', foreground: '22aaff', fontStyle: 'bold' },
      { token: 'keyword.proximity', foreground: 'aa77ff', fontStyle: 'bold' },
      { token: 'keyword.unique', foreground: 'aa11aa', fontStyle: 'bold' },
      { token: 'keyword.wildcard', foreground: '22aaff' },
      { token: 'string', foreground: '669900' },
      { token: 'number', foreground: '22cccc' },
      { token: 'number.proximity', foreground: '22cccc' },
      { token: 'type.identifier', foreground: '22cccc', fontStyle: 'bold' },
      { token: 'identifier', foreground: '667799' }
    ],
    colors: {}
  });

  // Dark theme variant
  monaco.editor.defineTheme('booleanSearchThemeDark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword.boolean', foreground: '22aaff', fontStyle: 'bold' },
      { token: 'keyword.proximity', foreground: 'aa77ff', fontStyle: 'bold' },
      { token: 'keyword.unique', foreground: 'aa11aa', fontStyle: 'bold' },
      { token: 'keyword.wildcard', foreground: '22aaff' },
      { token: 'string', foreground: '669900' },
      { token: 'number', foreground: '22cccc' },
      { token: 'number.proximity', foreground: '22cccc' },
      { token: 'type.identifier', foreground: '22cccc', fontStyle: 'bold' },
      { token: 'identifier', foreground: '667799' }
    ],
    colors: {}
  });
};

function BooleanQueryEditorPanelContent(props: BooleanQueryEditorProps, ref: any ): JSX.Element {
  const editorRef = useRef<any>(null);
  const theme = useTheme();
  const user = currentUser();
  
  const heightCalculationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastContentRef = useRef<string>(props.content || "");
  const isUserTypingRef = useRef<boolean>(false);
  const setContentRef = useRef(props.setContent);

  const [editorHeight, setEditorHeight] = useState<number>(props.height || 50);
  const [editorLang, ] = useState(props.language || 'infegyBoolean');
  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(!props.content?.trim());
  const [editorFocused, setEditorFocused] = useState<boolean>(false);

  // Update setContent ref without causing re-renders
  useEffect(() => {
    setContentRef.current = props.setContent;
  }, [props.setContent]);

  const isDarkMode = user?.getPreference(UserPreference.THEME)?.toLowerCase().includes("dark");
  const monacoTheme:string = isDarkMode ? "booleanSearchThemeDark" : "booleanSearchTheme";

  // Register the custom language on component mount
  useEffect(() => {
    registerBooleanSearchLanguage();
  }, []);

  // Height calculation based on line count taking line wrapping into account
  const calculateHeight = useCallback((content: string, containerWidth = 600) => {
    const lines = content.split('\n');
    const lineHeight = 24;
    const avgCharWidth = 10;
    const charsPerLine = Math.floor(containerWidth / avgCharWidth) || 100;
    const wrappedLineCount = lines.reduce((total, line) => {
      return total + Math.max(1, Math.ceil(line.length / charsPerLine));
    }, 0);
    const minHeight = 30;
    const maxHeight = 300;
    return Math.max(minHeight, Math.min(maxHeight, (wrappedLineCount * lineHeight)));
  }, []);

  // Debounced height calculation to prevent excessive re-renders
  const debouncedHeightCalculation = useCallback((content: string, containerWidth?: number) => {
    if (heightCalculationTimeoutRef.current) {
      clearTimeout(heightCalculationTimeoutRef.current);
    }

    heightCalculationTimeoutRef.current = setTimeout(() => {
      const newHeight = calculateHeight(content, containerWidth);
      setEditorHeight(newHeight);
    }, 100);
  }, [calculateHeight]);

  // Update the editor content when the content prop changes (only when not user typing)
  useEffect(() => {
    // Only update if content actually changed and user is not currently typing
    if (props.content !== lastContentRef.current && !isUserTypingRef.current) {
      lastContentRef.current = props.content;

      // Update editor value if it exists and content differs from current editor value
      if (editorRef.current) {
        const currentEditorValue = editorRef.current.getValue();
        if (currentEditorValue !== props.content) {
          // Temporarily disable typing detection to prevent interference
          isUserTypingRef.current = true;
          editorRef.current.setValue(props.content);
          setTimeout(() => {
            isUserTypingRef.current = false;
          }, 50);
        }
      }

      // Recalculate height with debouncing
      debouncedHeightCalculation(props.content, editorRef.current?.getLayoutInfo().width);
      
      // Update placeholder visibility
      setShowPlaceholder(!props.content?.trim());
    }
  }, [props.content, debouncedHeightCalculation]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (heightCalculationTimeoutRef.current) {
        clearTimeout(heightCalculationTimeoutRef.current);
      }
    };
  }, []);

  // Stable editor mount handler with no dependencies that change
  const handleEditorDidMount = useCallback((editor: any) => {
    editorRef.current = editor;

    // Set initial content without triggering user typing flag
    if (props.content) {
      editor.setValue(props.content);
    }

    // Handle focus events for placeholder visibility
    editor.onDidFocusEditorWidget(() => {
      setEditorFocused(true);
    });

    editor.onDidBlurEditorWidget(() => {
      setEditorFocused(false);
    });

    // Handle content changes with user typing detection
    editor.onDidChangeModelContent(() => {
      // Mark that user is typing to prevent external updates from interfering
      isUserTypingRef.current = true;

      const newContent = editor.getValue();
      lastContentRef.current = newContent;

      // Update placeholder visibility based on content
      setShowPlaceholder(!newContent.trim());

      // Call the parent's setContent callback if provided
      if (setContentRef.current) {
        setContentRef.current(newContent);
      }

      // Debounce height calculation to prevent excessive updates during fast typing
      debouncedHeightCalculation(newContent, editor.getLayoutInfo().width);

      // Reset typing flag after a short delay
      setTimeout(() => {
        isUserTypingRef.current = false;
      }, 100);
    });

    // Initial height calculation
    const initialHeight = calculateHeight(props.content || "", editor.getLayoutInfo().width);
    setEditorHeight(initialHeight);
  }, []); // No dependencies to prevent re-creation

  useImperativeHandle(ref, () => ({
    getContent() {
      return editorRef.current ? editorRef.current.getValue() : lastContentRef.current;
    }
  }), []);

  const editorBorder = useMemo(() => {
    if (!props.errorStatus) {
      return `1px solid ${theme.colors.alpha.black[30]}`;
    } else if (props.errorStatus === QueryErrorStatus.ERROR) {
      return `2px solid ${theme.colors.namedColors.negative}`;
    } else if (props.errorStatus === QueryErrorStatus.LOADING) {
      return `2px solid ${theme.colors.primary.light}`;
    } else if (props.errorStatus === QueryErrorStatus.VERIFIED) {
      return `2px solid ${theme.colors.namedColors.positive}`;
    }
  }, [props.errorStatus, theme]);

  const boxStyles = {
      borderRadius: theme?.general?.borderRadius,
      border: editorBorder,
      backgroundColor: theme.colors.alpha.white[100],
      padding: "10px 13px",
      overflow: "hidden",
      position: "relative"
    };

  if (props.actions) {
    boxStyles["display"] = "grid";
    boxStyles["gridTemplateColumns"] = "1fr auto";
    boxStyles["alignItems"] = "center";
    boxStyles["gap"] = "0";
  }

  return (
  <Box sx={boxStyles}>
    <Box sx={{position: "relative"}}>
      {/* Placeholder overlay */}
      {showPlaceholder && !editorFocused && props.placeholder && (
        <Box
          sx={{
            position: "absolute",
            top: "0px",
            left: "0",
            right: "0",
            fontSize: 16,
            lineHeight: "16px",
            opacity: 0.7,
            color: theme.palette.text.disabled,
            pointerEvents: "none",
            zIndex: 1,
            paddingLeft: "13px", // Monaco editor margin offset
            paddingTop: "6px" // Vertical alignment with editor text
          }}
        >
          {props.placeholder}
        </Box>
      )}
      <Editor
        theme={monacoTheme}
        options={{
          minimap: { enabled: false },
          lineNumbers: "off",
          fontSize: 18,
          lineHeight: 24,
          readOnly: props.readOnly ?? false,
          wordWrap: 'on',
          scrollBeyondLastLine: false,
          scrollbar: {
            vertical: 'hidden',
            horizontal: 'hidden',
            alwaysConsumeMouseWheel: false
          },
          quickSuggestions: false,
          contextmenu: false,
          overviewRulerBorder: false,
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          glyphMargin: false,
          folding: false,
          lineDecorationsWidth: 0,
          lineNumbersMinChars: 0,
          renderLineHighlight: 'none',
          automaticLayout: true,
          acceptSuggestionOnCommitCharacter: false,
          acceptSuggestionOnEnter: "off",
          accessibilitySupport: "off",
          autoIndent: "none",
          detectIndentation: false,
          dragAndDrop: false,
          formatOnPaste: false,
          formatOnType: false,
        }}
        height={`${editorHeight}px`}
        defaultLanguage={editorLang}
        defaultValue={props.content || ""}
        onMount={handleEditorDidMount} />
    </Box>
    {props.actions && (
      <Box>
        {props.actions}
      </Box>
    )}
  </Box>
  );
}

const BooleanQueryEditorPanel = forwardRef(BooleanQueryEditorPanelContent);

export default BooleanQueryEditorPanel;